import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { HabitatIntactnessExplainer } from '@/components/NCExplainers/HabitatIntactnessExplainer';
import { COMPLIANCE_LOGOS_HABITAT_INTACTNESS } from '@/config/constants';
import {
  getBenchmarkForConservationFact,
  getBenchmarkForConservationFromFacts,
} from '@/pages/shared/hooks/useBenchmarkForFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { HabitatIntactnessGraphTile } from './GraphTile';
import { HabitatIntactnessStatsTile } from './StatsTile';

export const HabitatIntactness = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.habitatIntactness.labels.habitatIntactness')}>
      <HabitatIntactnessCard />
    </PlotCardWrapper>
  );
};

const HabitatIntactnessCard = () => {
  const { t } = useTranslation();

  const factNames = {
    graph: IndicatorName.habitat_intactness_total_historic_graph,
    latestAnalysis: IndicatorName.habitat_intactness_total,
    trend: IndicatorName.habitat_intactness_total_previous_5y_pct_change,
    benchmark: IndicatorName.habitat_intactness_total_benchmark,
    baseline: IndicatorName.habitat_intactness_baseline_total,
  };

  const facts = usePlotFacts(Object.values(factNames));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factNames.graph)?.value;
  const latestFact = getFactFromFactArray<number>(facts, factNames.latestAnalysis);

  const benchmarks = getBenchmarkForConservationFromFacts({ facts, factType: factNames.benchmark });
  const benchmark = getBenchmarkForConservationFact({
    fact: latestFact,
    benchmarks,
  });

  const trendFact = getFactFromFactArray<number>(facts, factNames.trend);
  const baselineFact = getFactFromFactArray<number>(facts, factNames.baseline);

  const hasChartData = !isNullish(graphData);
  const hasStatsData = !isNullish(latestFact?.value) || !isNullish(baselineFact?.value) || !isNullish(trendFact?.value);

  if (!hasChartData && !hasStatsData) {
    return null;
  }

  return (
    <NCCard explainer={<HabitatIntactnessExplainer />} data-testid='habitat-intactness-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.habitatIntactness.labels.habitatIntactness')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <HabitatIntactnessGraphTile data={graphData} benchmarks={benchmarks} benchmark={benchmark} />
        <HabitatIntactnessStatsTile
          latestFact={latestFact}
          baselineFact={baselineFact}
          trendFact={trendFact}
          benchmark={benchmark}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_HABITAT_INTACTNESS} />
    </NCCard>
  );
};
